import * as THREE from 'three';
import { MAP, STREET_LAMP } from '../utils/constants';
import type { NeonLightData } from './wetRoadMaterial';

export type StreetLampHandle = {
  group: THREE.Group;
  lights: THREE.PointLight[];
  getNearbyLights: (x: number, z: number, radius: number) => NeonLightData[];
};

function createLampPost(): THREE.Group {
  const group = new THREE.Group();

  const poleMat = new THREE.MeshStandardMaterial({
    color: 0x2a2a2a,
    roughness: 0.7,
    metalness: 0.3,
  });

  const pole = new THREE.Mesh(
    new THREE.CylinderGeometry(0.15, 0.2, STREET_LAMP.HEIGHT, 6),
    poleMat,
  );
  pole.position.y = STREET_LAMP.HEIGHT / 2;
  pole.castShadow = false;
  group.add(pole);

  const armMat = new THREE.MeshStandardMaterial({
    color: 0x3a3a3a,
    roughness: 0.6,
    metalness: 0.4,
  });

  const arm = new THREE.Mesh(
    new THREE.BoxGeometry(1.5, 0.1, 0.1),
    armMat,
  );
  arm.position.set(0.75, STREET_LAMP.HEIGHT - 0.2, 0);
  group.add(arm);

  const houseMat = new THREE.MeshStandardMaterial({
    color: 0x1a1a1a,
    roughness: 0.5,
    metalness: 0.5,
  });

  const housing = new THREE.Mesh(
    new THREE.BoxGeometry(0.6, 0.3, 0.4),
    houseMat,
  );
  housing.position.set(1.4, STREET_LAMP.HEIGHT - 0.35, 0);
  group.add(housing);

  const glowMat = new THREE.MeshBasicMaterial({
    color: STREET_LAMP.COLOR,
    transparent: true,
    opacity: 0.9,
  });

  const glow = new THREE.Mesh(
    new THREE.PlaneGeometry(0.5, 0.25),
    glowMat,
  );
  glow.position.set(1.4, STREET_LAMP.HEIGHT - 0.52, 0);
  glow.rotation.x = -Math.PI / 2;
  group.add(glow);

  const coneMat = new THREE.MeshBasicMaterial({
    color: STREET_LAMP.COLOR,
    transparent: true,
    opacity: 0.08,
    side: THREE.DoubleSide,
    depthWrite: false,
  });

  const cone = new THREE.Mesh(
    new THREE.ConeGeometry(3, 6, 8, 1, true),
    coneMat,
  );
  cone.position.set(1.4, STREET_LAMP.HEIGHT - 3.5, 0);
  cone.rotation.x = Math.PI;
  group.add(cone);

  return group;
}

export function createStreetLamps(): StreetLampHandle {
  const group = new THREE.Group();
  group.name = 'StreetLamps';

  const lights: THREE.PointLight[] = [];
  const lampPositions: THREE.Vector3[] = [];

  const { HALF, GRID, ROAD_WIDTH, SIDEWALK_WIDTH } = MAP;
  const roadWithSidewalk = ROAD_WIDTH + SIDEWALK_WIDTH * 2;
  const totalRoadSpace = (GRID + 1) * roadWithSidewalk;
  const inner = MAP.SIZE - totalRoadSpace;
  const blockW = inner / GRID;

  // Use emissive glow meshes instead of PointLights for massive perf gain.
  // Only place lamps on one side of each road and with wider spacing.
  const glowMat = new THREE.MeshBasicMaterial({
    color: STREET_LAMP.COLOR,
    transparent: true,
    opacity: 0.85,
  });
  const glowGeo = new THREE.SphereGeometry(0.6, 4, 4);

  const actualSpacing = STREET_LAMP.SPACING * 2;

  for (let i = 0; i <= GRID; i++) {
    const z = -HALF + i * (roadWithSidewalk + blockW) + roadWithSidewalk / 2;

    for (let x = -HALF + actualSpacing; x < HALF; x += actualSpacing) {
      const side = (Math.floor(x * 0.1) % 2 === 0) ? 1 : -1;
      const offsetZ = side * (ROAD_WIDTH * 0.5 + SIDEWALK_WIDTH + 0.5);
      const lamp = createLampPost();
      lamp.position.set(x, 0, z + offsetZ);
      lamp.rotation.y = side > 0 ? 0 : Math.PI;
      group.add(lamp);

      const glowBall = new THREE.Mesh(glowGeo, glowMat);
      glowBall.position.set(x + side * 1.4, STREET_LAMP.HEIGHT - 0.5, z + offsetZ);
      group.add(glowBall);
      lampPositions.push(glowBall.position.clone());
    }
  }

  for (let j = 0; j <= GRID; j++) {
    const x = -HALF + j * (roadWithSidewalk + blockW) + roadWithSidewalk / 2;

    for (let z = -HALF + actualSpacing; z < HALF; z += actualSpacing) {
      const side = (Math.floor(z * 0.1) % 2 === 0) ? 1 : -1;
      const offsetX = side * (ROAD_WIDTH * 0.5 + SIDEWALK_WIDTH + 0.5);
      const lamp = createLampPost();
      lamp.position.set(x + offsetX, 0, z);
      lamp.rotation.y = side > 0 ? Math.PI / 2 : -Math.PI / 2;
      group.add(lamp);

      const glowBall = new THREE.Mesh(glowGeo, glowMat);
      glowBall.position.set(x + offsetX, STREET_LAMP.HEIGHT - 0.5, z + side * 1.4);
      group.add(glowBall);
      lampPositions.push(glowBall.position.clone());
    }
  }

  const getNearbyLights = (x: number, z: number, radius: number): NeonLightData[] => {
    const nearby: NeonLightData[] = [];
    const radiusSq = radius * radius;

    for (let i = 0; i < lampPositions.length && nearby.length < 16; i++) {
      const pos = lampPositions[i];
      const dx = pos.x - x;
      const dz = pos.z - z;
      const distSq = dx * dx + dz * dz;

      if (distSq < radiusSq) {
        nearby.push({
          position: pos,
          color: new THREE.Color(STREET_LAMP.COLOR),
          intensity: STREET_LAMP.INTENSITY * (1 - Math.sqrt(distSq) / radius),
        });
      }
    }

    return nearby;
  };

  return { group, lights, getNearbyLights };
}
